import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface BentoGridProps {
    children: ReactNode;
    className?: string;
}

interface BentoItemProps {
    children: ReactNode;
    className?: string;
    span?: 'default' | 'wide' | 'tall' | 'large';
}

const spanClasses = {
    default: '',
    wide: 'md:col-span-2',
    tall: 'md:row-span-2',
    large: 'md:col-span-2 md:row-span-2',
};

export function BentoGrid({ children, className = '' }: BentoGridProps) {
    return (
        <div
            className={`
        grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 
        gap-4 md:gap-6 
        ${className}
      `}
        >
            {children}
        </div>
    );
}

export function BentoItem({
    children,
    className = '',
    span = 'default'
}: BentoItemProps) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className={`
        card-glow rounded-2xl overflow-hidden
        ${spanClasses[span]}
        ${className}
      `}
        >
            {children}
        </motion.div>
    );
}

// Pre-built Bento items for common use cases

export function BentoVideo({ videoId }: { videoId: string }) {
    return (
        <BentoItem span="wide" className="aspect-video">
            <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
            />
        </BentoItem>
    );
}

export function BentoCTA({
    title,
    description,
    buttonText,
    onClick
}: {
    title: string;
    description: string;
    buttonText: string;
    onClick?: () => void;
}) {
    return (
        <BentoItem className="p-8 flex flex-col justify-between">
            <div>
                <h3 className="text-xl font-bold text-white mb-2 font-display">
                    {title}
                </h3>
                <p className="text-zinc-400 text-sm">
                    {description}
                </p>
            </div>
            <button
                onClick={onClick}
                className="btn-primary mt-6 w-full"
            >
                {buttonText}
            </button>
        </BentoItem>
    );
}

export function BentoStats({
    stats
}: {
    stats: { label: string; value: string }[]
}) {
    return (
        <BentoItem className="p-6">
            <div className="grid grid-cols-2 gap-4 h-full">
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        className="flex flex-col justify-center items-center text-center"
                    >
                        <span className="text-2xl font-bold text-neon-green font-display">
                            {stat.value}
                        </span>
                        <span className="text-xs text-zinc-500 mt-1">
                            {stat.label}
                        </span>
                    </div>
                ))}
            </div>
        </BentoItem>
    );
}
